import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Grid, Typography, Divider } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { CompanyClusterProgrammerCompanyDto, CompanyDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import ClusterProgrammerCompanyAddEditDialog from './ClusterProgrammerCompanyAddEditDialog'
import { useAuth } from 'context'

interface ClusterProgrammerCompanyAddEditDialogProps {
  company: CompanyDto
}

export default function ClusterProgrammerCompanyListCard({ company }: ClusterProgrammerCompanyAddEditDialogProps) {
  const { permissions } = useAuth()

  const [isClusterProgrammerCompanySaving, setIsClusterProgrammerCompanySaving] = useState<boolean>(false)
  const [clusterProgrammerCompanyAddEditDialogOpen, setClusterProgrammerCompanyAddEditDialogOpen] = useState<boolean>(false)
  const [companyClusterProgrammerCompany, setCompanyClusterProgrammerCompany] = useState<CompanyClusterProgrammerCompanyDto>(
    new CompanyClusterProgrammerCompanyDto()
  )
  const [isGettingClusterProgrammerCompany, setIsGettingClusterProgrammerCompany] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingClusterProgrammerCompany(true)
      controllers
        .getCompanyClusterProgrammerInformation(company.companyGuid)
        .then(response => {
          setCompanyClusterProgrammerCompany(response.value)
        })
        .finally(() => {
          setIsGettingClusterProgrammerCompany(false)
        })
    }
  }, [company])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <ClusterProgrammerCompanyAddEditDialog
          initialValues={companyClusterProgrammerCompany}
          loading={isClusterProgrammerCompanySaving}
          onClose={() => {
            setClusterProgrammerCompanyAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsClusterProgrammerCompanySaving(true)
            controllers
              .updateCompanyClusterProgrammerCompanyInformation(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Cluster Programmer Company Information Saved Successfully!', { variant: 'success' })
                setCompanyClusterProgrammerCompany(response.value)
                setClusterProgrammerCompanyAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsClusterProgrammerCompanySaving(false)
              })
          }}
          open={clusterProgrammerCompanyAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Cluster Programmer Company
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setIsClusterProgrammerCompanySaving(false)
                      setClusterProgrammerCompanyAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingClusterProgrammerCompany && (
              <Grid
                item
                container
                alignItems='center'
                justifyContent='center'
                sx={{
                  my: 1
                }}
              >
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingClusterProgrammerCompany && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Cluster Programmer Company Code'
                    value={companyClusterProgrammerCompany.clusterProgrammerCompanyCode}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||This card only shows if the company is marked as a Cluster Programmer Company. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
